import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  Card,
  CardBody,
  Divider,
  Badge,
  Flex,
  Spinner,
  useColorModeValue,
  Tooltip,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Textarea,
  Select,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
} from "@chakra-ui/react";
import { FiMapPin, FiSettings, FiSave, FiRefreshCw, FiDownload, FiUpload, FiInfo } from "react-icons/fi";
import { useUserContext } from "../../../context/UserContext";
import { useGetLocationSettings, useUpdateLocationSettings } from "../../../services/configurationService";

const LocationSettings = () => {
  const [settings, setSettings] = useState({
    default_radius: 100,
    max_radius: 500,
    min_radius: 50,
    enforce_location: true,
    allow_remote_work: false,
    grace_period_minutes: 5,
    early_arrival_window_minutes: 60,
  });
  
  const [currentSettings, setCurrentSettings] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { user } = useUserContext();
  
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const cardBg = useColorModeValue("gray.50", "gray.700");

  // API hooks
  const {
    locationSettingsData,
    locationSettingsIsLoading,
    locationSettingsError,
    refetchLocationSettings
  } = useGetLocationSettings({ enabled: true });

  const {
    updateLocationSettingsPayload,
    updateLocationSettingsIsLoading,
    updateLocationSettingsError
  } = useUpdateLocationSettings(() => {
    refetchLocationSettings();
    toast({
      title: "Settings saved successfully",
      description: "Location settings have been updated",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  });

  // Load settings when API data changes
  useEffect(() => {
    if (locationSettingsData) {
      setCurrentSettings(locationSettingsData);
      setSettings(locationSettingsData);
    }
  }, [locationSettingsData]);

  // Handle API errors
  useEffect(() => {
    if (locationSettingsError) {
      toast({
        title: "Error loading settings",
        description: locationSettingsError,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [locationSettingsError, toast]);

  useEffect(() => {
    if (updateLocationSettingsError) {
      toast({
        title: "Error saving settings",
        description: updateLocationSettingsError,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [updateLocationSettingsError, toast]);

  const validateSettings = () => {
    const errors = {};
    
    if (settings.min_radius >= settings.default_radius) {
      errors.min_radius = "Minimum radius must be less than default radius";
    }
    
    if (settings.default_radius >= settings.max_radius) {
      errors.default_radius = "Default radius must be less than maximum radius";
    }
    
    if (settings.min_radius < 10) {
      errors.min_radius = "Minimum radius cannot be less than 10 meters";
    }
    
    if (settings.max_radius > 1000) {
      errors.max_radius = "Maximum radius cannot exceed 1000 meters";
    }
    
    if (settings.grace_period_minutes < 0 || settings.grace_period_minutes > 30) {
      errors.grace_period_minutes = "Grace period must be between 0 and 30 minutes";
    }
    
    if (settings.early_arrival_window_minutes < 0 || settings.early_arrival_window_minutes > 180) {
      errors.early_arrival_window_minutes = "Early arrival window must be between 0 and 180 minutes";
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateSettings()) {
      return;
    }
    
    updateLocationSettingsPayload(settings);
  };

  const handleReset = () => {
    if (currentSettings) {
      setSettings(currentSettings);
      setValidationErrors({});
    }
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'location-settings.json';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedSettings = JSON.parse(e.target.result);
          setSettings(importedSettings);
          toast({
            title: "Settings imported",
            description: "Location settings have been imported successfully",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        } catch (error) {
          toast({
            title: "Import failed",
            description: "Invalid file format",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      };
      reader.readAsText(file);
    }
  };

  if (locationSettingsIsLoading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" color="blue.500" />
        <Text mt={4} color="gray.600">Loading location settings...</Text>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Current Settings Overview */}
      <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between" align="center">
              <Heading size="md" color="gray.800">
                Current Settings
              </Heading>
              <HStack spacing={2}>
                <Tooltip label="Refresh settings">
                  <IconButton
                    icon={<FiRefreshCw />}
                    onClick={refetchLocationSettings}
                    variant="ghost"
                    size="sm"
                    aria-label="Refresh settings"
                  />
                </Tooltip>
                <Tooltip label="Export settings">
                  <IconButton
                    icon={<FiDownload />}
                    onClick={handleExport}
                    variant="ghost"
                    size="sm"
                    aria-label="Export settings"
                  />
                </Tooltip>
                <Tooltip label="Import settings">
                  <IconButton
                    icon={<FiUpload />}
                    onClick={onOpen}
                    variant="ghost"
                    size="sm"
                    aria-label="Import settings"
                  />
                </Tooltip>
              </HStack>
            </HStack>
            
            <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
              <Stat>
                <StatLabel>Default Radius</StatLabel>
                <StatNumber>{currentSettings?.default_radius || 0}m</StatNumber>
                <StatHelpText>Current setting</StatHelpText>
              </Stat>
              <Stat>
                <StatLabel>Enforce Location</StatLabel>
                <StatNumber>
                  <Badge colorScheme={currentSettings?.enforce_location ? "green" : "red"}>
                    {currentSettings?.enforce_location ? "Yes" : "No"}
                  </Badge>
                </StatNumber>
                <StatHelpText>Location tracking</StatHelpText>
              </Stat>
              <Stat>
                <StatLabel>Grace Period</StatLabel>
                <StatNumber>{currentSettings?.grace_period_minutes || 0} min</StatNumber>
                <StatHelpText>Late arrival tolerance</StatHelpText>
              </Stat>
              <Stat>
                <StatLabel>Early Arrival Window</StatLabel>
                <StatNumber>{currentSettings?.early_arrival_window_minutes || 0} min</StatNumber>
                <StatHelpText>{Math.floor((currentSettings?.early_arrival_window_minutes || 0) / 60)}h {(currentSettings?.early_arrival_window_minutes || 0) % 60}m</StatHelpText>
              </Stat>
            </SimpleGrid>
            
            {currentSettings?.updated_at && (
              <Text fontSize="sm" color="gray.500" textAlign="center">
                Last updated: {new Date(currentSettings.updated_at).toLocaleString()}
              </Text>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Settings Form */}
      <Card bg={bgColor} border="1px solid" borderColor={borderColor}>
        <CardBody>
          <VStack spacing={6} align="stretch">
            <Heading size="md" color="gray.800">
              Location Settings Configuration
            </Heading>
            
            <Text color="gray.600" fontSize="sm">
              Configure location tracking parameters, radius settings, and work location policies for your organization.
            </Text>

            <Divider />

            {/* Radius Settings */}
            <Box>
              <Heading size="sm" color="gray.700" mb={4}>
                Radius Configuration
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <FormControl isInvalid={!!validationErrors.min_radius}>
                  <FormLabel>Minimum Radius (meters)</FormLabel>
                  <NumberInput
                    value={settings.min_radius}
                    onChange={(value) => setSettings({ ...settings, min_radius: parseInt(value) || 0 })}
                    min={10}
                    max={settings.default_radius - 1}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  {validationErrors.min_radius && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {validationErrors.min_radius}
                    </Text>
                  )}
                </FormControl>

                <FormControl isInvalid={!!validationErrors.default_radius}>
                  <FormLabel>Default Radius (meters)</FormLabel>
                  <NumberInput
                    value={settings.default_radius}
                    onChange={(value) => setSettings({ ...settings, default_radius: parseInt(value) || 0 })}
                    min={settings.min_radius + 1}
                    max={settings.max_radius - 1}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  {validationErrors.default_radius && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {validationErrors.default_radius}
                    </Text>
                  )}
                </FormControl>

                <FormControl isInvalid={!!validationErrors.max_radius}>
                  <FormLabel>Maximum Radius (meters)</FormLabel>
                  <NumberInput
                    value={settings.max_radius}
                    onChange={(value) => setSettings({ ...settings, max_radius: parseInt(value) || 0 })}
                    min={settings.default_radius + 1}
                    max={1000}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  {validationErrors.max_radius && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {validationErrors.max_radius}
                    </Text>
                  )}
                </FormControl>
              </SimpleGrid>
            </Box>

            <Divider />

            {/* Location Policies */}
            <Box>
              <Heading size="sm" color="gray.700" mb={4}>
                Location Policies
              </Heading>
              <VStack spacing={4} align="stretch">
                <FormControl display="flex" alignItems="center">
                  <FormLabel htmlFor="enforce-location" mb="0">
                    Enforce Location Tracking
                  </FormLabel>
                  <Switch
                    id="enforce-location"
                    isChecked={settings.enforce_location}
                    onChange={(e) => setSettings({ ...settings, enforce_location: e.target.checked })}
                    colorScheme="blue"
                  />
                </FormControl>
                
                <FormControl display="flex" alignItems="center">
                  <FormLabel htmlFor="allow-remote-work" mb="0">
                    Allow Remote Work
                  </FormLabel>
                  <Switch
                    id="allow-remote-work"
                    isChecked={settings.allow_remote_work}
                    onChange={(e) => setSettings({ ...settings, allow_remote_work: e.target.checked })}
                    colorScheme="blue"
                  />
                </FormControl>

                <FormControl isInvalid={!!validationErrors.grace_period_minutes}>
                  <FormLabel>Grace Period (minutes)</FormLabel>
                  <NumberInput
                    value={settings.grace_period_minutes}
                    onChange={(value) => setSettings({ ...settings, grace_period_minutes: parseInt(value) || 0 })}
                    min={0}
                    max={30}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Time allowed after scheduled start time before marking as late
                  </Text>
                  {validationErrors.grace_period_minutes && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {validationErrors.grace_period_minutes}
                    </Text>
                  )}
                </FormControl>

                <FormControl isInvalid={!!validationErrors.early_arrival_window_minutes}>
                  <FormLabel>Early Arrival Window (minutes)</FormLabel>
                  <NumberInput
                    value={settings.early_arrival_window_minutes}
                    onChange={(value) => setSettings({ ...settings, early_arrival_window_minutes: parseInt(value) || 0 })}
                    min={0}
                    max={180}
                    defaultValue={60}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Time window before scheduled start time to allow early check-in
                  </Text>
                  {validationErrors.early_arrival_window_minutes && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {validationErrors.early_arrival_window_minutes}
                    </Text>
                  )}
                </FormControl>
              </VStack>
            </Box>

            <Divider />

            {/* Action Buttons */}
            <HStack spacing={4} justify="flex-end">
              <Button
                variant="outline"
                onClick={handleReset}
                leftIcon={<FiRefreshCw />}
                isDisabled={updateLocationSettingsIsLoading}
              >
                Reset Changes
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleSave}
                leftIcon={<FiSave />}
                isLoading={updateLocationSettingsIsLoading}
                loadingText="Saving..."
              >
                Save Settings
              </Button>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Import Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Import Location Settings</ModalHeader>
          <ModalBody>
            <VStack spacing={4}>
              <Text color="gray.600">
                Select a JSON file containing location settings to import.
              </Text>
              <Input
                type="file"
                accept=".json"
                onChange={handleImport}
                variant="filled"
              />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default LocationSettings; 